import React, { useState, useEffect } from 'react';
import {
  User,
  Lock,
  Users,
  GraduationCap,
  Briefcase,
  Award,
  Shield,
  Key,
  Save,
} from 'lucide-react';
import userApi, { UserProfile } from '../api/userApi';
import apiClient from '../api/apiClient';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileInfo from '../components/profile/ProfileInfo';
import ProfileSecurity from '../components/profile/ProfileSecurity';
import PersonalTab from '../components/hris/employee/form/PersonalTab';
import FamilyTab from '../components/hris/employee/form/FamilyTab';
import EducationTab from '../components/hris/employee/form/EducationTab';
import AccountTab from '../components/hris/employee/form/AccountTab';
import EmploymentDetailTab from '../components/hris/employee/detail/EmploymentDetailTab';
import CertificationDetailTab from '../components/hris/employee/detail/CertificationDetailTab';
import PunishmentDetailTab from '../components/hris/employee/detail/PunishmentDetailTab';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

export default function Profile() {
  const { t, language } = useLanguage();
  const { showSuccess, showError } = useToast();
  const { updateUser: updateAuthUser } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isEmployee, setIsEmployee] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState({});

  const BASE_TABS = [
    {
      id: 'info',
      label: language === 'id' ? 'Informasi Pribadi' : 'Personal Information',
      icon: User,
    },
    { id: 'security', label: language === 'id' ? 'Keamanan' : 'Security', icon: Lock },
  ];

  const EMPLOYEE_TABS = [
    { id: 'personal', label: 'Data Pribadi', icon: User },
    { id: 'family', label: 'Keluarga', icon: Users },
    { id: 'education', label: 'Pendidikan', icon: GraduationCap },
    { id: 'employment', label: 'Kepegawaian', icon: Briefcase },
    { id: 'certification', label: 'Sertifikasi', icon: Award },
    { id: 'account', label: 'Akun Login', icon: Key },
    { id: 'punishment', label: 'Punishment', icon: Shield },
  ];

  const TABS = isEmployee ? EMPLOYEE_TABS : BASE_TABS;

  const loadEmployeeData = React.useCallback(
    async (employeeId: number) => {
      try {
        // Use user-specific endpoints that don't require HRIS permissions
        const [empDetail, refData] = await Promise.all([
          apiClient.get('/user/employee-data'),
          apiClient.get('/user/reference-data'),
        ]);

        const depts = refData.departments || [];
        const poss = refData.positions || [];

        setEmployeeData(empDetail);
        setDepartments(depts);
        setPositions(poss);
        setEmployees([]); // Not needed for profile, only used for supervisor selection

        // Don't set formData here - let the useEffect handle it
      } catch (error: any) {
        // Show error to user so they know what's happening
        showError('Gagal memuat data pegawai: ' + (error.response?.data?.error || error.message));
      }
    },
    [showError]
  );

  const loadUserData = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await userApi.getProfile();

      // Debug: Check user permissions (silent fail)
      try {
        await apiClient.get('/user/debug-permissions');
        await apiClient.get('/user/permissions-simple');
      } catch {
        // Silent fail for debug endpoints
      }

      // Normalize birth_date to date-only (YYYY-MM-DD) to avoid timezone shifts
      const normalizedUser = {
        ...data,
        birth_date: data.birth_date ? String(data.birth_date).split('T')[0] : data.birth_date,
      } as any;
      setUser(normalizedUser);

      // Check if user is an employee (has employee_id)
      if (data.employee_id) {
        setIsEmployee(true);
        setActiveTab('personal'); // Set to first employee tab
        await loadEmployeeData(data.employee_id);
      } else {
        setIsEmployee(false);
        setActiveTab('info'); // Set to regular profile tab
      }
      return data;
    } catch (error: any) {
      const errorMessage = error.message || 'Gagal memuat data profil';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError, loadEmployeeData]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Sync formData when employeeData changes
  useEffect(() => {
    if (employeeData && Object.keys(employeeData).length > 0) {
      const initialFormData = {
        ...employeeData,
        ktp_url: employeeData.ktp_file_url || employeeData.ktp_url || '',
        base_salary: employeeData.base_salary?.toString() || '',
        competency_level: employeeData.competency_level || 3,
        family_members: employeeData.family_members || [],
        education_history: employeeData.education_history || [],
        certifications: employeeData.certifications || [],
        nip: employeeData.employee_id || '',
        birth_date: employeeData.birth_date ? String(employeeData.birth_date).split('T')[0] : '',
      };
      setFormData(initialFormData);
    }
  }, [employeeData]);

  const getDepartmentName = (deptId: number | null | undefined) => {
    if (!deptId) return '-';
    const dept = departments.find((d: any) => d.id === deptId);
    return dept?.name || '-';
  };

  const getPositionName = (posId: number | null | undefined) => {
    if (!posId) return '-';
    const pos = positions.find((p: any) => p.id === posId);
    return pos?.name || '-';
  };

  const getEmployeeName = (empId: number | null | undefined) => {
    if (!empId) return '-';
    const emp = employees.find((e: any) => e.id === empId);
    return emp?.full_name || '-';
  };

  const handleUpdateProfile = async (updatedData: any) => {
    setSaving(true);
    try {
      const data = await userApi.updateProfile(updatedData);

      setUser(prev => (prev ? { ...prev, ...data } : data));

      // Update AuthContext so header will reflect the changes
      updateAuthUser({
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        nik: data.nik,
        avatar_url: data.avatar_url,
      });

      showSuccess('Profil berhasil diperbarui');
    } catch (error: any) {
      showError(error.message || 'Gagal memperbarui profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (passwordData: {
    current_password: string;
    new_password: string;
  }) => {
    setSaving(true);
    try {
      await userApi.changePassword(passwordData);
      showSuccess('Password berhasil diubah');
    } catch (error: any) {
      showError(error.message || 'Gagal mengubah password');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    // Clear error when user types
    if (errors[field as keyof typeof errors]) {
      setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmployeeUpdate = async (tabId: string) => {
    if (!user?.employee_id) {
      showError('Employee ID tidak ditemukan');
      return;
    }

    // Validate current tab
    if (!validateEmployeeTab(tabId)) {
      // Show detailed validation errors
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        showError(errorMessages.join(' | '));
      } else {
        showError('Mohon lengkapi data yang wajib diisi');
      }
      return;
    }

    setSaving(true);
    try {
      const tabFields = getEmployeeTabFields(tabId);

      // Use user-specific endpoint that doesn't require HRIS permissions
      await apiClient.put('/user/employee-data', tabFields);

      // Reload employee data
      await loadEmployeeData(user.employee_id);

      // Try to also update the actual user profile record so the profile header reflects changes
      // (some setups store employee and user separately).
      let updatedProfile = null as any;
      try {
        const profilePayload: any = {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
        };
        // Send birth_date as date-only (YYYY-MM-DD) to avoid UTC datetime conversion
        if (formData.birth_date)
          profilePayload.birth_date = String(formData.birth_date).split('T')[0];
        // prefer photo_url as avatar
        if (formData.photo_url) profilePayload.avatar_url = formData.photo_url;

        updatedProfile = await userApi.updateProfile(profilePayload);
      } catch {
        // Not critical — continue with local update fallback
        // Try to reload profile anyway; some backends auto-sync
        try {
          updatedProfile = await loadUserData();
        } catch {
          updatedProfile = null;
        }
      }

      // Sync AuthContext and local `user` so header/profile card updates immediately.
      // Prefer values returned from profile endpoint, but fall back to the current formData
      // (useful when the backend stores employee and user separately).
      // Prefer the local `formData.birth_date` (what the user just picked) when available
      // because the backend may convert a date-only value to UTC datetime and shift the day.
      const profileBirth =
        updatedProfile && (updatedProfile as any).birth_date
          ? String((updatedProfile as any).birth_date).split('T')[0]
          : null;

      const newUserData = {
        full_name: (updatedProfile && updatedProfile.full_name) || formData.full_name,
        email: (updatedProfile && updatedProfile.email) || formData.email,
        phone: (updatedProfile && updatedProfile.phone) || formData.phone,
        nik: (updatedProfile && (updatedProfile as any).nik) || formData.nik,
        avatar_url:
          (updatedProfile &&
            ((updatedProfile as any).avatar_url || (updatedProfile as any).photo_url)) ||
          formData.photo_url ||
          formData.avatar_url,
        // Use the freshly-picked value when present to avoid timezone-based day shifts
        birth_date: formData.birth_date || profileBirth || null,
      } as any;

      // Update local page state
      setUser(prev =>
        prev
          ? { ...prev, ...newUserData }
          : { ...(newUserData as any), id: user?.id || 0, role: user?.role || '' }
      );

      // Update global AuthContext/localStorage
      updateAuthUser(newUserData as any);

      showSuccess(
        `Data ${EMPLOYEE_TABS.find(t => t.id === tabId)?.label || 'pegawai'} berhasil disimpan`
      );
    } catch (error: any) {
      showError(error?.response?.data?.error || error?.message || 'Gagal menyimpan data pegawai');
    } finally {
      setSaving(false);
    }
  };

  const getEmployeeTabFields = (tabId: string) => {
    // Only editable tabs: personal, family, education, account
    switch (tabId) {
      case 'personal':
        return {
          full_name: formData.full_name,
          photo_url: formData.photo_url,
          nik: formData.nik,
          birth_place: formData.birth_place,
          birth_date:
            formData.birth_date && formData.birth_date.trim() !== '' ? formData.birth_date : null,
          address: formData.address,
          marital_status:
            formData.marital_status && formData.marital_status.trim() !== ''
              ? formData.marital_status
              : null,
          phone: formData.phone,
          email: formData.email,
          emergency_contact_name: formData.emergency_contact_name,
          emergency_contact_phone: formData.emergency_contact_phone,
          ktp_file_url: formData.ktp_url,
          gender: formData.gender && formData.gender.trim() !== '' ? formData.gender : null,
        };
      case 'family':
        return {
          family_members: formData.family_members || [],
        };
      case 'education':
        return {
          education_history: formData.education_history || [],
        };
      case 'account':
        return {
          nip: formData.nip || null,
          password: formData.password || null,
        };
      default:
        return {};
    }
  };

  const validateEmployeeTab = (tabId: string) => {
    const newErrors: Record<string, string> = {};

    // Only validate editable tabs
    if (tabId === 'personal') {
      if (!formData.full_name || formData.full_name.trim() === '') {
        newErrors.full_name = 'Nama lengkap harus diisi';
      }
      if (!formData.email || formData.email.trim() === '') {
        newErrors.email = 'Email harus diisi';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Format email tidak valid';
      }
      if (formData.nik && formData.nik.trim() !== '') {
        if (!/^\d{16}$/.test(formData.nik)) {
          newErrors.nik = 'NIK harus 16 digit angka';
        }
      }
    }

    if (tabId === 'account') {
      // For profile edit, password is optional but if provided, must be at least 6 characters
      if (formData.password && formData.password.trim() !== '') {
        if (formData.password.length < 6) {
          newErrors.password = 'Password minimal 6 karakter';
        }
      }
    }

    // Family and Education tabs don't have required fields for validation

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  if (loading && !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Gagal memuat data profil</p>
      </div>
    );
  }

  const renderTabContent = () => {
    if (isEmployee) {
      // Render employee tabs - some editable, some read-only
      switch (activeTab) {
        case 'personal':
          // Editable: Data Pribadi
          if (loading || Object.keys(formData).length === 0) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading personal data...
              </div>
            );
          }
          return <PersonalTab formData={formData} handleChange={handleChange} errors={errors} />;
        case 'family':
          // Editable: Keluarga
          if (loading || Object.keys(formData).length === 0) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading family data...
              </div>
            );
          }
          return <FamilyTab formData={formData} setFormData={setFormData} />;
        case 'education':
          // Editable: Pendidikan
          if (loading || Object.keys(formData).length === 0) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading education data...
              </div>
            );
          }
          return <EducationTab formData={formData} setFormData={setFormData} />;
        case 'employment':
          // Read-only: Kepegawaian
          if (loading || !employeeData) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading employment data...
              </div>
            );
          }
          return (
            <EmploymentDetailTab
              employeeData={employeeData}
              getDepartmentName={getDepartmentName}
              getPositionName={getPositionName}
              getEmployeeName={getEmployeeName}
            />
          );
        case 'certification':
          // Read-only: Sertifikasi
          if (loading || !employeeData) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading certification data...
              </div>
            );
          }
          return <CertificationDetailTab employeeData={employeeData} />;
        case 'account':
          // Editable: Akun Login
          if (loading || Object.keys(formData).length === 0) {
            return (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading account data...
              </div>
            );
          }
          return (
            <AccountTab
              formData={formData}
              handleChange={handleChange}
              errors={errors}
              isEdit={true}
            />
          );
        case 'punishment':
          // Read-only: Punishment
          return <PunishmentDetailTab employeeId={user?.employee_id?.toString() || null} />;
        default:
          return <div className="text-center py-8 text-gray-500">Tab not found</div>;
      }
    } else {
      // Render regular profile tabs
      switch (activeTab) {
        case 'info':
          return <ProfileInfo user={user} onUpdate={handleUpdateProfile} loading={saving} />;
        case 'security':
          return <ProfileSecurity onChangePassword={handleChangePassword} loading={saving} />;
        default:
          return <div className="text-center py-8 text-gray-500">Tab not found</div>;
      }
    }
  };

  // Define which tabs are editable for employees
  const isEditableTab = (tabId: string): boolean => {
    const editableTabs = ['personal', 'family', 'education', 'account'];
    return editableTabs.includes(tabId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
        <p className="text-gray-500 mt-1">{t('profile.subtitle')}</p>
      </div>

      {/* Profile Header */}
      <ProfileHeader user={user} onUpdateAvatar={handleUpdateProfile} />

      {/* Tabs */}
      <div className="bg-white border rounded-lg">
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setErrors({}); // Clear errors when switching tabs
                  }}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                    isActive
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 animate-fadeIn">{renderTabContent()}</div>
      </div>

      {/* Save Button for Editable Employee Tabs */}
      {isEmployee && isEditableTab(activeTab) && (
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => handleEmployeeUpdate(activeTab)}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-95 flex items-center gap-2 disabled:opacity-50 transition-all duration-300"
          >
            <Save className="w-4 h-4" />
            {saving
              ? 'Menyimpan...'
              : `Simpan ${EMPLOYEE_TABS.find(t => t.id === activeTab)?.label || 'Data'}`}
          </button>
        </div>
      )}
    </div>
  );
}
